
import { Minimize2, Maximize2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/lib/useMediaQuery";
import { PromptInput } from "./PromptInput";

/** Delay before auto-minimizing on mobile when input loses focus (ms) */
const MOBILE_AUTO_MINIMIZE_DELAY = 5000;

interface ImmersiveToolbarProps {
  spaceName: string | null;
  prompt: string;
  onPromptChange: (value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  isMinimized: boolean;
  inputFocused: boolean;
  onToggleMinimize: () => void;
  onInputFocus: () => void;
  onInputBlur: () => void;
  isReadonly?: boolean;
}

export function ImmersiveToolbar({
  spaceName,
  prompt,
  onPromptChange,
  onSubmit,
  isLoading,
  isMinimized,
  inputFocused,
  onToggleMinimize,
  onInputFocus,
  onInputBlur,
  isReadonly = false,
}: ImmersiveToolbarProps) {
  const isMobile = useIsMobile();

  // Auto-minimize on mobile when input loses focus
  const [autoMinimized, setAutoMinimized] = useState(false);
  const autoMinimizeTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!isMobile) {
      setAutoMinimized(false);
      return;
    }

    // Clear existing timer
    if (autoMinimizeTimer.current) {
      clearTimeout(autoMinimizeTimer.current);
      autoMinimizeTimer.current = null;
    }

    if (inputFocused || isMinimized) {
      setAutoMinimized(false);
    } else {
      // Start timer to auto-minimize
      autoMinimizeTimer.current = window.setTimeout(() => {
        setAutoMinimized(true);
      }, MOBILE_AUTO_MINIMIZE_DELAY);
    }

    return () => {
      if (autoMinimizeTimer.current) clearTimeout(autoMinimizeTimer.current);
    };
  }, [isMobile, inputFocused, isMinimized]);

  // Effective minimized state: manual or auto-minimized
  const effectiveMinimized = isMinimized || autoMinimized;

  // When expanding from auto-minimize, clear the auto state
  const handleExpand = () => {
    setAutoMinimized(false);
    if (isMinimized) {
      onToggleMinimize();
    }
  };

  return (
    <div
      className={cn(
        "absolute bottom-2 md:bottom-4 z-20 pointer-events-none",
        // On mobile, position the minimized state to bottom-right to avoid FAB conflict
        effectiveMinimized
          ? "right-2 md:right-4"
          : "left-2 md:left-4 right-2 md:right-4",
      )}
    >
      <div
        className={cn(
          "pointer-events-auto transition-all duration-300 ease-in-out",
          effectiveMinimized
            ? "w-auto inline-flex"
            : "flex items-end justify-between",
          "bg-white/80 backdrop-blur-sm border border-sands-200 rounded-xl shadow-sm",
          inputFocused ? "opacity-100" : "opacity-70 hover:opacity-100",
        )}
        onMouseEnter={(e) => {
          if (!inputFocused) {
            e.currentTarget.style.opacity = "1";
          }
        }}
        onMouseLeave={(e) => {
          if (!inputFocused) {
            e.currentTarget.style.opacity = "";
          }
        }}
        onTouchStart={() => {
          // Reset auto-minimize on touch
          if (autoMinimized) {
            setAutoMinimized(false);
          }
        }}
      >
        {effectiveMinimized ? (
          <button
            onClick={handleExpand}
            className="flex items-center gap-2 px-3 py-2.5 md:py-2 min-h-[44px] text-sands-600 hover:text-sands-800 transition-colors"
            title="Expand toolbar"
          >
            <Maximize2 className="w-4 h-4" />
            <span className="text-sm font-medium">Expand</span>
          </button>
        ) : (
          <>
            {/* Left side: Space name */}
            <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 flex-1 min-w-0">
              {spaceName && (
                <span className="text-xs font-medium text-sands-500 whitespace-nowrap max-w-[80px] md:max-w-[120px] truncate shrink-0">
                  {spaceName}
                </span>
              )}
              <div className="w-px h-5 md:h-6 bg-sands-200 shrink-0" />
              <div className="flex-1 min-w-0">
                <PromptInput
                  value={prompt}
                  onChange={onPromptChange}
                  onSubmit={onSubmit}
                  isLoading={isLoading}
                  compact
                  onFocus={onInputFocus}
                  onBlur={onInputBlur}
                  disabled={isReadonly}
                />
              </div>
            </div>
            <button
              onClick={onToggleMinimize}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] mr-1 text-sands-400 hover:text-sands-600 transition-colors"
              title="Minimize toolbar"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
